import { useEffect, useMemo, useState } from 'react';
import { AtrioDemo } from './components/AtrioDemo';
import { CanteraDemo } from './components/CanteraDemo';
import { ConsolidatedLeadForm } from './components/ConsolidatedLeadForm';
import { StateTicker } from './components/StateTicker';
import { content, type SiteDictionary, type SiteLanguage } from './content/site-content';
import { identityAssets } from './lib/assets';
import { useSiteExperience } from './motion/experience';

const layerKeys = ['l1', 'l2', 'l3', 'l4'] as const;
const methodKeys = ['p1', 'p2', 'p3', 'p4'] as const;

export function App() {
  const [language, setLanguage] = useState<SiteLanguage>('es');
  const [motionPaused, setMotionPaused] = useState(() =>
    typeof window === 'undefined'
      ? true
      : window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        window.sessionStorage.getItem('civili-motion') === 'paused'
  );
  const dictionary = useMemo(() => content[language] as SiteDictionary, [language]);
  const t = (key: string) => dictionary[key] ?? key;

  useSiteExperience();

  useEffect(() => {
    document.documentElement.lang = language === 'es' ? 'es-CO' : 'en';
    document.title = dictionary.documentTitle ?? 'CIVILI';
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute('content', dictionary.documentDescription ?? '');
  }, [dictionary, language]);

  // One switch for everything that moves on its own, which is what a visitor needs to stop the
  // ticker and both demos at once.
  useEffect(() => {
    document.documentElement.dataset.motion = motionPaused ? 'paused' : 'active';
    try {
      window.sessionStorage.setItem('civili-motion', motionPaused ? 'paused' : 'active');
    } catch {
      // Private browsing can refuse storage; the preference simply does not persist.
    }
  }, [motionPaused]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const follow = (event: MediaQueryListEvent) => {
      if (event.matches) setMotionPaused(true);
    };
    media.addEventListener?.('change', follow);
    return () => media.removeEventListener?.('change', follow);
  }, []);

  return (
    <>
      <a className="skip-link" href="#contenido">{t('nav.skip')}</a>

      <header className="site-header" data-header>
        <a className="brand-lockup" href="#inicio" aria-label={t('nav.brand')}>
          <img src={identityAssets.lockupHorizontal} alt="CIVILI" width="1436" height="346" />
        </a>
        <nav className="site-nav" aria-label={t('nav.label')}>
          <a href="#productos" data-nav-link>{t('nav.products')}</a>
          <a href="#capas" data-nav-link>{t('nav.layers')}</a>
          <a href="#metodo" data-nav-link>{t('nav.method')}</a>
        </nav>
        <div className="site-controls">
          <button
            className="icon-control"
            type="button"
            aria-pressed={motionPaused}
            aria-label={t(motionPaused ? 'nav.play' : 'nav.pause')}
            onClick={() => setMotionPaused((paused) => !paused)}
          >
            <span aria-hidden="true">{motionPaused ? '▶' : '❙❙'}</span>
          </button>
          <button
            className="lang-control"
            type="button"
            aria-label={language === 'es' ? 'Switch to English' : 'Cambiar a español'}
            onClick={() => setLanguage((current) => (current === 'es' ? 'en' : 'es'))}
          >
            {language === 'es' ? 'EN' : 'ES'}
          </button>
          <a className="header-cta" href="#contacto">{t('nav.cta')}</a>
        </div>
      </header>

      <main id="contenido">
        <section className="hero" id="inicio" aria-labelledby="hero-title" data-section>
          <h1 id="hero-title" className="hero__title">
            <span>{t('hero.title1')}</span>
            <span>{t('hero.title2')}</span>
            <em>{t('hero.title3')}</em>
          </h1>

          <StateTicker dictionary={dictionary} />

          <div className="hero__foot">
            <p className="hero__kicker">{t('hero.kicker')}</p>
            <div className="hero__actions">
              <a className="action action--solid" href="#contacto">
                <span>{t('hero.primary')}</span><span aria-hidden="true">↗</span>
              </a>
              <a className="action action--line" href="#productos">
                <span>{t('hero.secondary')}</span><span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>
        </section>

        <section
          className="showcase"
          id="productos"
          data-tone="ink"
          aria-labelledby="atrio-title"
          data-section
        >
          <p className="rule-label">{t('atrio.label')}</p>
          <div className="showcase__copy">
            <h2 id="atrio-title" className="showcase__name">{t('atrio.name')}</h2>
            <p className="showcase__line">{t('atrio.line')}</p>
            <p className="showcase__limit">
              <span>{t('demo.limitLabel')}</span>{t('atrio.limit')}
            </p>
          </div>
          <AtrioDemo dictionary={dictionary} paused={motionPaused} />
        </section>

        <section className="showcase" id="cantera" aria-labelledby="cantera-title" data-section>
          <p className="rule-label">{t('cantera.label')}</p>
          <div className="showcase__copy">
            <h2 id="cantera-title" className="showcase__name">{t('cantera.name')}</h2>
            <p className="showcase__line">{t('cantera.line')}</p>
            <p className="showcase__limit">
              <span>{t('demo.limitLabel')}</span>{t('cantera.limit')}
            </p>
          </div>
          <CanteraDemo dictionary={dictionary} paused={motionPaused} />
        </section>

        <section className="layers" id="capas" aria-labelledby="layers-title" data-section>
          <p className="rule-label">{t('layers.label')}</p>
          <h2 id="layers-title" className="section-title" data-reveal="">{t('layers.title')}</h2>
          <ol className="layer-rows">
            {layerKeys.map((key, index) => (
              <li className="layer-row" key={key}>
                <span className="layer-row__index" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="layer-row__name">{t(`layers.${key}name`)}</h3>
                <p className="layer-row__body">{t(`layers.${key}body`)}</p>
                <p className="layer-row__limit">
                  <span>{t('layers.limit')}</span>
                  {t(`layers.${key}limit`)}
                </p>
              </li>
            ))}
          </ol>
        </section>

        <section className="method" id="metodo" aria-labelledby="method-title" data-section>
          <p className="rule-label">{t('method.label')}</p>
          <h2 id="method-title" className="section-title" data-reveal="">{t('method.title')}</h2>

          {/* The billing boundary as a bar: the free half is filled, the paid half is outlined,
              and the split sits exactly where the invoice starts. */}
          <p className="method__bar" aria-hidden="true">
            <span className="method__bar-free">{t('method.free')}</span>
            <span className="method__bar-paid">{t('method.paid')}</span>
          </p>

          <ol className="method__steps">
            {methodKeys.map((key, index) => (
              <li className="method-step" data-paid={index > 1 ? '' : undefined} key={key}>
                <p className="method-step__meta">
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <strong>{t(index > 1 ? 'method.paid' : 'method.free')}</strong>
                </p>
                <h3>{t(`method.${key}title`)}</h3>
                <p>{t(`method.${key}body`)}</p>
              </li>
            ))}
          </ol>
          <p className="method__after">{t('method.after')}</p>
        </section>

        <section className="contact" id="contacto" aria-labelledby="contact-title" data-section>
          <div className="contact__copy">
            <h2 id="contact-title" className="section-title" data-reveal="">
              <span>{t('contact.title1')}</span> <em>{t('contact.title2')}</em>
            </h2>
            <p className="contact__terms" data-reveal="">{t('contact.terms')}</p>
            <p className="contact__direct" data-reveal="">
              <span>{t('contact.direct')}</span>{' '}
              <a href="mailto:accounts@civili.systems">accounts@civili.systems</a>
            </p>
            <img
              className="contact__mark"
              src={identityAssets.mark000}
              alt=""
              width="512"
              height="512"
              data-canonical-mark="000"
            />
          </div>
          <div className="contact__form" data-reveal="">
            <ConsolidatedLeadForm dictionary={dictionary} language={language} />
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="site-footer__brand">
          <a href="#inicio" aria-label={t('nav.brand')}>
            <img src={identityAssets.lockupHorizontal} alt="CIVILI" width="1436" height="346" />
          </a>
          <p>{t('footer.position')}</p>
        </div>
        <div className="site-footer__meta">
          <p>{t('footer.evidence')}</p>
          <p>{t('footer.company')}</p>
        </div>
        <nav className="social-nav" aria-label={t('footer.social')}>
          <a href="https://www.linkedin.com/company/civilisystems" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn CIVILI">LinkedIn ↗</a>
          <a href="https://www.instagram.com/civilisystems/" target="_blank" rel="noopener noreferrer" aria-label="Instagram CIVILI">Instagram ↗</a>
          <a href="https://www.facebook.com/profile.php?id=61592992467198" target="_blank" rel="noopener noreferrer" aria-label="Facebook CIVILI">Facebook ↗</a>
        </nav>
        {/* Las páginas legales son archivos estáticos, no vistas de esta SPA:
            por eso el enlace es una navegación de verdad y no un ancla. */}
        <nav className="legal-nav" aria-label={t('footer.legal')}>
          <a href={language === 'en' ? '/privacy' : '/privacidad'}>{t('footer.privacy')}</a>
          <a href={language === 'en' ? '/data-deletion' : '/eliminacion-de-datos'}>
            {t('footer.deletion')}
          </a>
        </nav>
        <p className="site-footer__status">{t('footer.status')}</p>
      </footer>
    </>
  );
}
